import { useEffect, useRef, useState } from "react";
import { Bot, Send, X } from "lucide-react";
import { API } from "@/constants/site";

const getSessionId = () => {
  let id = localStorage.getItem("cbh_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("cbh_chat_session", id);
  }
  return id;
};

const GREETING =
  "Hi, I'm the chatblackhole.com assistant — online 24/7. Ask me about websites, apps, AI chatbots, Matlab/Python help, research writing or tutoring.";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((msgs) => {
          const copy = [...msgs];
          copy[copy.length - 1] = {
            role: "assistant",
            content: copy[copy.length - 1].content + chunk,
          };
          return copy;
        });
      }
    } catch (err) {
      setMessages((msgs) => {
        const copy = [...msgs];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry — something went wrong. Reach us 24/7 on WhatsApp +923366757846.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-chat-toggle-btn"
        aria-label="Open AI chat assistant"
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-bold text-xs font-mono uppercase tracking-wider shadow-xl shadow-violet-600/30 transition-colors"
      >
        <Bot size={18} />
        AI Assistant
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-20 left-5 z-50 w-[22rem] max-w-[calc(100vw-2.5rem)] h-[28rem] max-h-[70vh] rounded-2xl border border-white/10 bg-[#171324] shadow-2xl shadow-violet-900/40 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#110e1a]">
            <div className="flex items-center gap-2">
              <Bot size={16} className="text-violet-400" />
              <span className="text-xs font-mono uppercase tracking-widest text-white">AI Assistant</span>
              <span className="w-2 h-2 rounded-full bg-emerald-400" title="Online 24/7" />
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="ai-chat-close-btn"
              aria-label="Close chat"
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={m.role === "user" ? "ai-chat-user-message" : "ai-chat-assistant-message"}
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-violet-600 text-white"
                      : "bg-black/40 border border-white/10 text-slate-200"
                  }`}
                >
                  {m.content || (busy && m.role === "assistant" ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="flex items-center gap-2 px-3 py-3 border-t border-white/10 bg-[#110e1a]">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="ai-chat-input"
              placeholder="Ask about a service..."
              className="flex-1 px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-violet-500 placeholder:text-slate-500"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="ai-chat-send-btn"
              aria-label="Send message"
              className="p-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white transition-colors"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
